import * as React from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { app } from '@microsoft/teams-js';
import { AcceptIcon, Flex, Text, CheckmarkCircleIcon, CircleIcon, Segment, DatepickerCalendar, CalendarIcon, ShiftActivityIcon, Loader, Button, SpeakerPersonIcon, Input, Alert, ExclamationTriangleIcon, Tooltip, Divider, RadioGroup, Dropdown } from '@fluentui/react-northstar';
import { getServicesExternal, getUserName, postAppointment, postStaffAvailability, getBookingsById } from '../../api/bookings-api';
import { getMeetingData } from '../../api/publicapp-api';
import Data from '../../constants/data';
import i18n from '../../i18n';
import * as moment from 'moment';

import './bookings-page.scss';

interface BookingsPageProps {}

interface RouteParams {
    ancora?: string;
}

interface TimeSlot {
    startTime: string; // "HH:mm:ss.ffffff"
    endTime: string;
}

interface BusinessHour {
    day: string; // ex: "monday"
    timeSlots: TimeSlot[];
}

interface Service {
    id: number;
    displayName: string;
    duration: number; // in minutes
    preBuffer: number; // in minutes
    postBuffer: number; // in minutes
    maximumAdvance: number; // in minutes
    defaultDuration: number;
    staffMemberIds: string[];
    generalAvailability: {
        availabilityType: string;
        businessHours: BusinessHour[];
    };
    customQuestions: any[];
    userName: string;
    userEmail: string;
}

interface StaffAvailability {
    staffId: string;
    availabilityItems: any[];
}

const BookingsPage: React.FC<BookingsPageProps> = ({ }) => {
    const { t } = useTranslation();
    const { ancora } = useParams<RouteParams>();

    const localizationStrings = {
        shortDays: i18n.language.includes("es") ? Data.shortDaysES : i18n.language.includes("en") ? Data.shortDaysEN : Data.shortDaysPT,
        months: i18n.language.includes("es") ? Data.monthFullNamesES : i18n.language.includes("en") ? Data.monthFullNamesEN : Data.monthFullNames,
    }

    const timezoneOptions = [
      {
        key: 'Central Brazilian Standard Time',
        header: '(UTC-04:00 Cuiabá)',
      },
      {
        key: 'E. South America Standard Time',
        header: '(UTC-03:00 Brasília)',
      },
    ];

    const [bookingId, setBookingId] = useState<string>();
    const [loadingServices, setLoadingServices] = useState<boolean>(true);
    const [bookingsServicesData, setBookingsServicesData] = useState<Service[]>([]);
    const [selectedService, setSelectedService] = useState<Service | null>(null);
    const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
    const [selectedTime, setSelectedTime] = useState<string | null>(null);
    const [navigatedDate, setNavigatedDate] = useState(new Date());
    const [staffAvailability, setStaffAvailability] = useState<StaffAvailability[] | null>(null);
    const [loadingTimeSlots, setLoadingTimeSlots] = useState<boolean>(true);
    const [filteredTimeSlots, setFilteredTimeSlots] = useState<{ time: string; staffIds: string[] }[]>([]);
    const [slotError, setSlotError] = useState<string | null>(null);

    const [loadingScheduleAppointment, setLoadingScheduleAppointment] = useState<boolean>(false);
    const [scheduledAppointment, setScheduledAppointment] = useState<boolean>(false);

    const [customerName, setCustomerName] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerAddress, setCustomerAddress] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerNotes, setCustomerNotes] = useState('');
    const [customQuestions, setCustomQuestions] = useState<Record<string, string>>({});

    const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);
    const [selectedTimezone, setSelectedTimezone] = useState(timezoneOptions[0]);

    const [error, setError] = useState<string>();

    moment.locale(i18n.language);

    const getTimeSlotsFromStaffAvailability = (
        availability: StaffAvailability[] | null,
        selectedDate: Date,
        durationInMinutes: number
    ): { time: string; staffIds: string[] }[] => {
        if (!availability || !availability.length) return [];

        const now = moment();
        const isToday = moment(selectedDate).isSame(now, 'day');
        const slotsMap: Record<string, Set<string>> = {};

        const dayOfWeek = moment(selectedDate).clone().locale('en').format('dddd').toLowerCase();

        let serviceEndTime: moment.Moment | null = null;
        if (selectedService?.generalAvailability?.businessHours) {
            const dayAvailability = selectedService.generalAvailability.businessHours.find(
                bh => bh.day.toLowerCase() === dayOfWeek
            );

            if (dayAvailability && dayAvailability.timeSlots.length > 0) {
                const latestEndTimeStr = dayAvailability.timeSlots[dayAvailability.timeSlots.length - 1].endTime;
                const [hours, minutes] = latestEndTimeStr.split(':');
                serviceEndTime = moment(selectedDate).hours(Number(hours)).minutes(Number(minutes)).seconds(0);
            }
        }

        for (const staff of availability) {
            const availableItems = staff.availabilityItems?.filter(item => item.status === 0) ?? [];

            for (const item of availableItems) {
                const start = moment(item.startDateTime.dateTime);
                const end = moment(item.endDateTime.dateTime);

                let slotTime = start.clone();

                const preBuffer = selectedService.preBuffer || 0;
                const postBuffer = selectedService.postBuffer || 0;

                while (slotTime.clone().add(durationInMinutes + postBuffer, 'minutes').isSameOrBefore(end)) {
                    const realStartTime = slotTime.clone().subtract(preBuffer, 'minutes');
                    const realEndTime = slotTime.clone().add(durationInMinutes + postBuffer, 'minutes');

                    if (realStartTime.isBefore(start)) {
                        slotTime.add(durationInMinutes, 'minutes');
                        continue;
                    }

                    if (!isToday || slotTime.isSameOrAfter(now)) {
                        if (serviceEndTime && realEndTime.isAfter(serviceEndTime)) {
                            break;
                        }

                        const timeStr = slotTime.format('HH:mm');
                        if (!slotsMap[timeStr]) {
                            slotsMap[timeStr] = new Set();
                        }
                        slotsMap[timeStr].add(staff.staffId);
                    }

                    const prev = slotTime.clone();
                    slotTime.add(durationInMinutes, 'minutes');
                    if (slotTime.isSameOrBefore(prev)) break;
                }
            }
        }

        return Object.entries(slotsMap)
            .map(([time, staffSet]) => ({
                time,
                staffIds: Array.from(staffSet),
            }))
            .sort((a, b) => moment(a.time, 'HH:mm').diff(moment(b.time, 'HH:mm')));
    };

    const timeSlots: { time: string; staffIds: string[] }[] = selectedService && selectedDate && staffAvailability
        ? getTimeSlotsFromStaffAvailability(staffAvailability, selectedDate, selectedService.duration)
        : [];

    const fetchStaffAvailability = async (service: Service, date: Date) => {
        if (!service || !date) return;

        try {
            const dayOfWeek = moment(date).clone().locale('en').format('dddd').toLowerCase();

            let startDateTime = moment(date).startOf('day');
            let endDateTime = moment(date).endOf('day');

            const businessHourForDay = service.generalAvailability?.businessHours?.find(
                bh => bh.day.toLowerCase() === dayOfWeek
            );

            if (businessHourForDay && businessHourForDay.timeSlots.length > 0) {
                const firstSlot = businessHourForDay.timeSlots[0];
                const lastSlot = businessHourForDay.timeSlots[businessHourForDay.timeSlots.length - 1];

                const [startHour, startMinute] = firstSlot.startTime.split(':');
                const [endHour, endMinute] = lastSlot.endTime.split(':');

                startDateTime = moment(date)
                    .hour(Number(startHour))
                    .minute(Number(startMinute))
                    .second(0);

                endDateTime = moment(date)
                    .hour(Number(endHour))
                    .minute(Number(endMinute))
                    .second(0);
            }

            const request = {
                bookingsId: bookingId,
                staffIds: service.staffMemberIds,
                startDateTime: {
                    dateTime: startDateTime.format('YYYY-MM-DDTHH:mm:ss'),
                    timeZone: selectedTimezone?.key,
                },
                endDateTime: {
                    dateTime: endDateTime.format('YYYY-MM-DDTHH:mm:ss'),
                    timeZone: selectedTimezone?.key,
                },
            };

            const result = await postStaffAvailability(request);
            setStaffAvailability(result.data);
        } catch (err) {
            console.error('Erro ao buscar disponibilidade:', err);
        } finally {
            setLoadingTimeSlots(false);
        }
    };

    const buildAppointmentPayload = (
        selectedService: Service,
        selectedDate: Date,
        selectedTime: string,
    ) => {
        const start = moment(selectedDate).startOf('day').add(moment.duration(selectedTime));
        const end = start.clone().add(selectedService.duration, 'minutes');

        return {
            startDateTime: {
                dateTime: start.format('YYYY-MM-DDTHH:mm:ss'),
                timeZone: selectedTimezone?.key,
            },
            endDateTime: {
                dateTime: end.format('YYYY-MM-DDTHH:mm:ss'),
                timeZone: selectedTimezone?.key,
            },
            bookingsId: bookingId,
            serviceId: selectedService.id.toString(),
            staffMemberIds: selectedStaffIds.length === 1
                ? selectedStaffIds
                : [selectedStaffIds[Math.floor(Math.random() * selectedStaffIds.length)]],
            customers: [
                {
                    name: customerName,
                    emailAddress: customerEmail,
                    phone: customerPhone,
                    notes: customerNotes,
                    answeredCustomQuestions: selectedService.customQuestions.map((q) => ({
                        questionId: q.questionId,
                        question: q.question,
                        answerInputType: q.answerInputType,
                        answerOptions: q.answerOptions ?? [],
                        isRequired: q.isRequired,
                        answer: q.answerInputType === 'Text' ? (customQuestions[q.questionId] ?? '') : '',
                        selectedOptions: q.answerInputType === 'RadioButton' ? [(customQuestions[q.questionId] ?? '')] : [],
                    })),
                    location: {
                        displayName: customerAddress,
                        address: {
                            street: customerAddress,
                            type: "Other"
                        }
                    },
                    smsNotificationsEnabled: false,
                    instanceId: "",
                    price: 0,
                    priceType: "SERVICEDEFAULTPRICETYPES_NOT_SET"
                }
            ],
            isLocationOnline: false,
            smsNotificationsEnabled: false,
            verificationCode: "",
            trackingDataId: "",
            bookingFormInfoList: [],
            price: 0,
            priceType: "SERVICEDEFAULTPRICETYPES_NOT_SET",
            isAllDay: false,
            additionalRecipients: []
        };
    };

    const handleSchedule = async () => {
        if (!selectedService || !selectedDate || !selectedTime || !bookingId) return;

        setLoadingScheduleAppointment(true);

        const start = moment(selectedDate).startOf('day').add(moment.duration(selectedTime));
        const end = start.clone().add(selectedService.duration, 'minutes');

        try {
            const request = {
                bookingsId: bookingId,
                staffIds: selectedService.staffMemberIds,
                startDateTime: {
                    dateTime: start.format('YYYY-MM-DDTHH:mm:ss'),
                    timeZone: selectedTimezone?.key,
                },
                endDateTime: {
                    dateTime: end.format('YYYY-MM-DDTHH:mm:ss'),
                    timeZone: selectedTimezone?.key,
                },
            };

            const response = await postStaffAvailability(request);
            const availability: StaffAvailability[] = response.data;

            const availableStaff = availability.filter(staff =>
                staff.availabilityItems?.some(item => {
                    const itemStart = moment(item.startDateTime.dateTime);
                    const itemEnd = moment(item.endDateTime.dateTime);
                    return item.status === 0 && itemStart.isSameOrBefore(start) && itemEnd.isSameOrAfter(end);
                })
            ).map(s => s.staffId);

            if (!availableStaff.length) {
                setSlotError(`O horário ${selectedTime} não está mais disponível. Por favor, selecione outro.`);
                setLoadingScheduleAppointment(false);

                setSelectedTime(null);
                setSelectedStaffIds([]);
                setFilteredTimeSlots(prev => prev.filter(slot => slot.time !== selectedTime));
                return;
            }

            setSelectedStaffIds(availableStaff);

            const appointmentPayload = buildAppointmentPayload(selectedService, selectedDate, selectedTime);
            const result = await postAppointment(appointmentPayload);

            if (result.data) {
                setScheduledAppointment(true);
                setError(undefined);
            } else {
                setScheduledAppointment(false);
                setError("Houve um erro ao criar o seu agendamento, tente novamente.");
            }
        } catch (err) {
            console.error('Erro ao agendar:', err);
            setError('Erro ao processar o agendamento. Tente novamente.');
        } finally {
            setLoadingScheduleAppointment(false);
        }
    };

    const hasAllRequiredCustomQuestions = (): boolean => {
        if (!selectedService?.customQuestions) return true;

        return selectedService.customQuestions
            .filter(q => q.isRequired)
            .every(q => (customQuestions[q.questionId] ?? '').trim() !== '');
    };

    const resetBookingPageState = () => {
        setSelectedService(null);
        setSelectedDate(new Date());
        setSelectedTime(null);
        setNavigatedDate(new Date());
        setStaffAvailability(null);
        setLoadingTimeSlots(false);
        setLoadingScheduleAppointment(false);
        setScheduledAppointment(false);
        setCustomQuestions({});
        setSelectedStaffIds([]);
        setError(undefined);
        setSlotError(undefined);
    };

    useEffect(() => {
        const fetchMeetingData = async () => {
            try {
                const result = await getMeetingData(ancora);

                if (result.data) {
                    setBookingId(result.data.bookingsId);
                    fetchBookings(result.data.bookingsId);
                }
            } catch (error) {
                console.error('Erro ao carregar meeting data:', error);
            }
        }

        const fetchBookings = async (bookingId: string) => {
            if (!bookingId) {
                setError("Erro ao obter o serviço do Bookings.");
                setLoadingServices(false);
                return;
            }

            try {
                const result = await getBookingsById(bookingId);

                if (result.data) {
                    setSelectedTimezone(timezoneOptions.find(t => t.key === result.data.businessTimeZone));
                    fetchServices(bookingId);
                }
            } catch (error) {
                console.error('Erro ao carregar bookings:', error);
            }
        }

        const fetchServices = async (bookingId: string) => {
            if (!bookingId) {
                setError("Erro ao obter o serviço do Bookings.");
                setLoadingServices(false);
                return;
            }

            try {
                const result = await getServicesExternal(bookingId);

                if (result.data?.length) {
                    setBookingsServicesData(result.data);
                    setLoadingServices(false);
                } else {
                    setError("Não foram encontrados serviços para este Bookings.");
                    setLoadingServices(false);
                }   
            } catch (error) {
                console.error('Erro ao carregar serviços:', error);
                setError("Erro ao carregar serviços do Bookings.");
                setLoadingServices(false);
            }
        };

        const fetchUserName = async () => {
            try {
                const result = await getUserName();

                if (result.data) {
                    setCustomerName(result.data);
                }
            } catch (error) {
                console.error('Erro ao carregar nome do usuário', error);
            }
        }

        app.initialize();
        app.getContext().then((context: app.Context) => {
            setCustomerEmail(context?.user?.loginHint);
            fetchMeetingData();
            fetchUserName();
        });
    }, []);

    useEffect(() => {
        if (!selectedService || !selectedDate) return;

        fetchStaffAvailability(selectedService, selectedDate);
    }, [selectedService, selectedDate, selectedTimezone]);

    useEffect(() => {
        if (!selectedService || !selectedDate || !staffAvailability) return;

        const slots = getTimeSlotsFromStaffAvailability(
            staffAvailability,
            selectedDate,
            selectedService.duration
        );

        setFilteredTimeSlots(slots);
    }, [selectedService, selectedDate, staffAvailability]);

    const renderServiceContent = (service: Service, isSelected: boolean) => (
        <Flex gap="gap.medium" column>
            <Flex space="between" vAlign="center">
                <Tooltip trigger={<Text size="large" weight="light" content={service.displayName} />} content={service.displayName} />
                
                {isSelected ? <CheckmarkCircleIcon size="large" /> : <CircleIcon outline size="large" />}
            </Flex>
            <Flex className="duration">
                <Text size="small" weight="semibold" content={`${service.duration} minutos`} />
            </Flex>
        </Flex>
    );

    const renderScheduledContent = () => (
        <Flex gap="gap.medium" column>
            <Flex gap="gap.medium" hAlign="center" column>
                <Text className="customer-name" weight="light" content={`Reserva para ${customerName}`} />
                <Text weight="semibold" size="large" content={selectedService?.displayName} />
            </Flex>

            <Flex className="schedule-info" gap="gap.large" space="between" vAlign="center">
                <Flex fill gap="gap.medium" column>
                    <Flex gap="gap.medium" vAlign="center">
                        <CalendarIcon outline />
                        <Text className="date-info" content={moment(selectedDate).format('dddd, MMMM D, YYYY')} />
                    </Flex>
                    
                    <Flex gap="gap.medium" vAlign="center">
                        <ShiftActivityIcon outline />
                        <Text content={`${selectedTime} (${selectedService?.duration} minutos)`} />
                    </Flex>
                </Flex>

                <Divider vertical />

                <Flex fill gap="gap.medium" vAlign="start" column>
                    <Button content="Novo agendamento" onClick={resetBookingPageState} primary />
                </Flex>
            </Flex>
        </Flex>
    );

    return (
        <div className="bookings-page">
            <div className="bookings-container">
                {error &&
                    <Alert danger content={error} icon={<ExclamationTriangleIcon outline />} />
                }

                {!scheduledAppointment ?
                    <>
                        <div className="bookings-services">
                            <Flex className="bookings-services-pickers" gap="gap.medium" vAlign="center">
                                {loadingServices ? <Loader label="Carregando serviços" /> :
                                    <>
                                        {bookingsServicesData.map((service: Service) => (
                                            <Segment
                                                key={service.id}
                                                className={`picker ${selectedService?.id === service.id ? 'selected' : ''}`}
                                                color="brand"
                                                content={renderServiceContent(service, selectedService?.id === service.id)}
                                                onClick={() => {
                                                    if (selectedService?.id === service.id) {
                                                        setLoadingTimeSlots(true);
                                                        setSelectedService(null);
                                                        setSelectedDate(new Date());
                                                        setSelectedTime(null);
                                                    } else {
                                                        setLoadingTimeSlots(true);
                                                        setSelectedService(service);
                                                        setSelectedDate(new Date());
                                                        setSelectedTime(null);
                                                    }
                                                }}
                                            />
                                        ))}
                                    </>
                                }
                            </Flex>
                        </div>

                        <div className={`bookings-calendar-container ${!selectedService ? 'disabled' : ''}`}>
                            <Flex gap="gap.large" fill column>
                                <Flex gap="gap.medium" fill>
                                    <Flex.Item size="size.half">
                                        <Flex gap="gap.medium" column>
                                            <Flex gap="gap.medium" vAlign="center">
                                                <CalendarIcon outline />
                                                <Text size="large" weight="semibold" content="Data" />
                                            </Flex>

                                            <DatepickerCalendar
                                                {...localizationStrings}
                                                className="calendar"
                                                selectedDate={selectedDate}
                                                navigatedDate={navigatedDate}
                                                onDateChange={(_, data) => {
                                                    const newDate = data.value;
                                                    setSelectedDate(newDate.originalDate);
                                                    setSelectedTime(null);
                                                    setLoadingTimeSlots(true);
                                                    console.log("Data selecionada:", newDate);
                                                }}
                                                calendarCellButton={{
                                                    children: (ComponentType, props) => {
                                                        const dateMoment = moment(props['aria-label'], 'MMMM D, YYYY', i18n.language);
                                                        if (!dateMoment.isValid()) return <ComponentType {...props} />;

                                                        const today = moment().startOf('day');
                                                        const selected = selectedDate ? moment(selectedDate).startOf('day') : null;
                                                        const date = dateMoment.startOf('day');

                                                        const maxAdvanceInDays = selectedService?.maximumAdvance
                                                            ? Math.floor(selectedService.maximumAdvance / (60 * 24))
                                                            : null;
                                                        const maxAdvanceDate = maxAdvanceInDays
                                                            ? moment().add(maxAdvanceInDays, 'days').startOf('day')
                                                            : null;

                                                        const isWeekend = date.day() === 0 || date.day() === 6;
                                                        const isBeforeToday = date.isBefore(today);
                                                        const isAfterMaxAdvance = maxAdvanceDate ? date.isAfter(maxAdvanceDate) : false;
                                                        const isToday = date.isSame(today);
                                                        const isSelected = selected && date.isSame(selected);

                                                        const disabled = isWeekend || isBeforeToday || isAfterMaxAdvance;

                                                        const className = `${props.className ?? ''} ${isToday ? 'today-button' : ''} ${isSelected ? 'selected-button' : ''} ${disabled ? 'disabled-button' : ''}`.trim();

                                                        return <ComponentType {...props} disabled={disabled} className={className} />;
                                                    }
                                                }}
                                                shortDays={localizationStrings.shortDays}
                                                months={localizationStrings.months}
                                                firstDayOfWeek={0}
                                                minDate={new Date()}
                                            />
                                        </Flex>
                                    </Flex.Item>

                                    <Flex.Item size="size.half">
                                        <Flex gap="gap.medium" column>
                                            <Flex gap="gap.medium" vAlign="center">
                                                <ShiftActivityIcon outline />
                                                <Text size="large" weight="semibold" content="Horário" />
                                            </Flex>

                                            {!selectedService ?
                                                <Text content="Selecione um serviço e uma data para ver os horários disponíveis." />
                                                :
                                                <>
                                                    {slotError && (
                                                        <Alert danger content={slotError} />
                                                    )}

                                                    <Flex className="time-slot-container" wrap gap="gap.small">
                                                        {loadingTimeSlots ? (
                                                            <Loader label="Carregando horários disponíveis..." />
                                                        ) : !timeSlots.length ? (
                                                            <Text content="Não há disponibilidade de horário nesta data." />
                                                        ) : (
                                                            <>
                                                                {filteredTimeSlots.map(({ time, staffIds }) => (
                                                                    <div
                                                                        key={time}
                                                                        className={`time-slot ${selectedTime === time ? 'selected' : ''}`}
                                                                        onClick={async () => {
                                                                            if (!bookingId || !selectedService || !selectedDate) return;

                                                                            const start = moment(selectedDate).startOf('day').add(moment.duration(time));
                                                                            const end = start.clone().add(selectedService.duration, 'minutes');

                                                                            setSelectedTime(time);

                                                                            try {
                                                                                const request = {
                                                                                    bookingsId: bookingId,
                                                                                    staffIds: selectedService.staffMemberIds,
                                                                                    startDateTime: {
                                                                                        dateTime: start.format('YYYY-MM-DDTHH:mm:ss'),
                                                                                        timeZone: selectedTimezone?.key,
                                                                                    },
                                                                                    endDateTime: {
                                                                                        dateTime: end.format('YYYY-MM-DDTHH:mm:ss'),
                                                                                        timeZone: selectedTimezone?.key,
                                                                                    },
                                                                                };

                                                                                const response = await postStaffAvailability(request);
                                                                                const availability: StaffAvailability[] = response.data;

                                                                                const availableStaff = availability.filter(staff =>
                                                                                    staff.availabilityItems?.some(item => {
                                                                                        const itemStart = moment(item.startDateTime.dateTime);
                                                                                        const itemEnd = moment(item.endDateTime.dateTime);
                                                                                        return item.status === 0 && itemStart.isSameOrBefore(start) && itemEnd.isSameOrAfter(end);
                                                                                    })
                                                                                ).map(s => s.staffId);

                                                                                if (availableStaff.length > 0) {
                                                                                    setSelectedStaffIds(availableStaff);
                                                                                    setSlotError(null);
                                                                                    console.log(`Staff válidos para ${time}:`, availableStaff);
                                                                                } else {
                                                                                    setFilteredTimeSlots(prev => prev.filter(slot => slot.time !== time));
                                                                                    setSelectedTime(null);
                                                                                    setSelectedStaffIds([]);
                                                                                    setSlotError(`O horário ${time} não está mais disponível. Por favor, selecione outro.`);
                                                                                    console.warn(`Nenhum staff disponível no horário ${time}`);
                                                                                }
                                                                            } catch (err) {
                                                                                console.error("Erro ao validar horário com staffAvailability:", err);
                                                                                setSlotError("Erro ao validar o horário selecionado. Tente novamente.");
                                                                            }
                                                                        }}
                                                                    >
                                                                        {time}
                                                                    </div>
                                                                ))}
                                                            </>
                                                        )}
                                                    </Flex>
                                                </>
                                            }

                                        </Flex>
                                    </Flex.Item>
                                </Flex>

                                <Flex className="timezone" gap="gap.smaller" vAlign="center">
                                    <Text content={`Os horários estão no fuso horário de ${selectedTimezone?.header}`} />
                                </Flex>

                                <Flex className="form-fields" gap="gap.medium" column>
                                    <Flex gap="gap.medium" vAlign="center">
                                        <SpeakerPersonIcon outline />
                                        <Text size="large" weight="semibold" content="Detalhes do agendamento" />
                                    </Flex>

                                    <Flex className="fields" gap="gap.medium">
                                        <Flex.Item size="size.half">
                                            <Flex gap="gap.medium" column>
                                                <Input className={`${!!customerName ? 'readonly' : ''}`} fluid label="Nome" value={customerName} onChange={(_, data) => setCustomerName(data.value)} required />
                                            </Flex>
                                        </Flex.Item>
                                        <Flex.Item size="size.half">
                                            <Flex gap="gap.smaller" column>
                                                <Input fluid label="E-mail" value={customerEmail} onChange={(_, data) => setCustomerEmail(data.value)} />
                                            </Flex>
                                        </Flex.Item>
                                    </Flex>

                                    {!!selectedService?.customQuestions?.length &&
                                        <Flex className="form-fields" gap="gap.medium" column>
                                            <Flex className="fields" gap="gap.medium" column>
                                                {selectedService?.customQuestions?.map((question) => (
                                                    <React.Fragment key={question.questionId}>
                                                        {question.answerInputType === 'Text' && (
                                                            <Input
                                                                key={question.questionId}
                                                                label={question.displayName}
                                                                required={question.isRequired}
                                                                value={customQuestions[question.questionId] || ''}
                                                                onChange={(_, data) => {
                                                                    setCustomQuestions(prev => ({
                                                                        ...prev,
                                                                        [question.questionId]: data.value
                                                                    }));
                                                                }}
                                                                fluid
                                                            />
                                                        )}

                                                        {question.answerInputType === 'RadioButton' && (
                                                            <>
                                                                <Text content={question.displayName} />
                                                                <RadioGroup
                                                                    items={question.answerOptions.map(o => ({
                                                                        key: o,
                                                                        label: o,
                                                                        value: o,
                                                                    }))}
                                                                    onCheckedValueChange={(_, data) => {
                                                                        setCustomQuestions(prev => ({
                                                                            ...prev,
                                                                            [question.questionId]: data.value,
                                                                        }));
                                                                    }}
                                                                />
                                                            </>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </Flex>
                                        </Flex>
                                    }
                                </Flex>
                            </Flex>
                        </div>

                        <div className="footer-container">
                            {!scheduledAppointment ?
                                <Button
                                    className={`schedule-btn ${loadingScheduleAppointment ? 'disabled' : ''}`}
                                    disabled={!(selectedService && selectedDate && selectedTime && customerName.trim() && hasAllRequiredCustomQuestions())}
                                    loading={loadingScheduleAppointment}
                                    onClick={handleSchedule}
                                    content="Agendar"
                                    primary
                                />
                                :
                                <Button
                                    className={`scheduled-btn disabled`}
                                    icon={<AcceptIcon />}
                                    content="Sua reserva foi agendada!"
                                    primary
                                />
                            }
                        </div>
                    </>    
                    :
                    <div className="scheduled-appointment">
                        <Alert success content="Agradecemos o seu agendamento conosco! Você receberá uma mensagem de confirmação por email em breve" />

                        <Segment
                            className={`scheduled`}
                            color="brand"
                            content={renderScheduledContent()}
                        />
                    </div>
                }
            </div>
        </div>
    );
};

export default BookingsPage;
